import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import styled, { withTheme } from 'styled-components';
import { withFormik } from 'formik';
import * as yup from 'yup';
import { withTranslation } from 'react-i18next';

import { capfl } from 'common/utils';
import SelectGroup from 'common/components/styleguide/form/SelectCustomGroup';
import Button from 'common/components/styleguide/Button';
import Numstepper from 'common/components/_base/NumStepper';
import Icon from 'common/components/styleguide/Icon';
import { togglePageModal, setPageData } from 'common/actions/PageActions';
import { fetchPossibleDeliveries } from 'common/actions/ReservationActions';

import { addToCart } from 'common/actions/CartActions';
import Reservation from '../Reservation';

import Hooks from './Hooks';
import { withCssUnit } from 'common/components/styleguide/utils/index';
import { getCartMaxPiecesCountSelector } from 'common/selectors/index';

const S = {};
S.Button = styled(Button)`
  && {
    height: 55px;
    padding: 0 10px;
    font-size: 0.8em;
    margin-left: 10px;
    @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.sm)}) {
      font-size: 0.9em;
      max-width: 280px;
      flex: 1 0 0;
    }
  }
`;

S.AddToCart = styled.section`
  display: flex;
  justify-content: flex-start;
  border-top: 2px solid #f9f9f9;
  border-bottom: 2px solid #f9f9f9;
  padding: 20px 0;
  font-weight: bolder;
  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.md)}) {
    border: none;
    padding: 0;
  }
`;

S.TextUnAvailable = styled.section`
  border: 1px solid rgba(0, 0, 0, 0.08);
  text-align: center;
  padding: 10px 0;
`;

class DetailForm extends PureComponent {
  onShowReservationModalClick = (e) => {
    e.preventDefault();
    const { data, showReservationModal } = this.props;
    showReservationModal(data);
  }
  render() {
    const {
      data,
      values,
      errors,
      touched,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
      isSubmitting,
      isValid,
      setSizeForReservation,
      setCountForReservation,
      cartMaxPiecesCount,
      t,
      theme
    } = this.props;
    const { store = [], productHooks, isOrderable } = data;
    if (isOrderable && !_.isEmpty(store)) {
      let selectedStore = null;
      if (values.size) {
        const selectedSize = values.size;
        selectedStore = _.find(store, obj => obj.id === selectedSize.value);
      }
      let cartMaxPieces = selectedStore.count
      if (selectedStore && cartMaxPiecesCount > 0 && cartMaxPiecesCount < selectedStore.count) {
        cartMaxPieces = cartMaxPiecesCount;
      }
      return (
        <section>
          <form onSubmit={handleSubmit}>
            {store.length > 1 ? (
              <section>
                <SelectGroup
                  selectProps={{
                    id: 'size',
                    name: 'size',
                    value: values.size ? values.size : null,
                    touched: touched.size,
                    options: _.map(store, item => {
                      return { value: item.id, label: item.name };
                    }),
                    onChange: values => {
                      setSizeForReservation(values);
                    },
                    setFieldValue,
                    setFieldTouched
                  }}
                  error={touched.size && errors.size ? errors.size : ''}
                  label={capfl(t('size'))}
                />
              </section>
            ) : (
              ''
            )}
            <S.AddToCart>
              {selectedStore ? (
                <Numstepper
                  min={1}
                  max={cartMaxPieces}
                  name="count"
                  startValue={1}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  customStyle={{
                    height: '55px'
                  }}
                  onChange={value => {
                    setCountForReservation(value);
                  }}
                  prevText="-"
                  nextText="+"
                />
              ) : (
                ''
              )}
              <S.Button
                type="submit"
                disabled={!isValid || isSubmitting}
                buttonType="secondary"
                customStyle={`color: ${_.get(theme, 'colors.primary')};`}
              >
                {isSubmitting ? (
                  <Icon className="nc-circle-02-1" />
                ) : (
                  capfl(t('add to cart'))
                )}
              </S.Button>
              {/* <S.Button onClick={this.onShowReservationModalClick}>
                {capfl(t('store reservation'))}
              </S.Button> */}
            </S.AddToCart>
          </form>
          <Reservation data={data} />
          {!_.isEmpty(productHooks) ? <Hooks data={productHooks} /> : ''}
        </section>
      );
    }
    return (
      <S.TextUnAvailable>
        {capfl(t("sorry, you can't order this product right now"))}
      </S.TextUnAvailable>
    );
  }
}

const getFormMessages = t => {
  return {
    store: { required: capfl(`${t('size')} ${t('is required')}!`) },
    count: {
      required: capfl(`${t('count')} ${t('is required')}!`)
    }
  };
};

const formikEnhancer = withFormik({
  isInitialValid: true,
  enableReinitialize: true,
  mapPropsToValues: props => {
    const { data } = props;
    const { store } = data;
    let propsToReturn = {
      size:
        _.isArray(store) && store.length === 1
          ? { value: store[0].id, label: store[0].name }
          : null,
      count: 1
    };
    return propsToReturn;
  },
  validationSchema: ({ t }) => {
    const messages = getFormMessages(t);
    return yup.object().shape({
      size: yup
        .object()
        .nullable()
        .required(messages.store.required),
      count: yup.number().required(messages.count.required)
    });
  },
  handleSubmit: async (values, { props, setSubmitting, setStatus }) => {
    const { data, productHooks, showProductHooksModal, addItemToCart } = props;
    const { id } = data;
    const { size, count } = values;
    addItemToCart(id, size.value, count);
    if (!_.isEmpty(productHooks)) {
      showProductHooksModal();
    }
    setSubmitting(false);
  }
});

const EnhancedDetailForm = formikEnhancer(DetailForm);

const makeMapStateToProps = () => {
  const getCartMaxPiecesCount = getCartMaxPiecesCountSelector();
  const mapStateToProps = (state, props) => {

    return {
      cartMaxPiecesCount: getCartMaxPiecesCount(state, props)
    }
  }

  return mapStateToProps;
}

const mapDispatchToProps = dispatch => {
  return {
    showProductHooksModal: () => {
      dispatch(togglePageModal('product_hooks', true));
    },
    addItemToCart: (id, sizeId, count) => {
      dispatch(addToCart({ productId: id, sizeId, count }));
    },
    showReservationModal: product => {
      dispatch(fetchPossibleDeliveries(product));
      dispatch(togglePageModal('product_reservation', true));
      dispatch(setPageData(false, 'reservation', 'success'));
    },
    setSizeForReservation: values => {
      dispatch(setPageData(values, 'reservation', 'size'));
    },
    setCountForReservation: value => {
      dispatch(setPageData(value, 'reservation', 'count'));
    }
  };
};

const EnhancedDetailFormConnected = connect(
  makeMapStateToProps,
  mapDispatchToProps
)(EnhancedDetailForm);

export default withTranslation()(withTheme(EnhancedDetailFormConnected));
